import React from 'react'
import clx from 'classnames'
import { ExchangeRateSourceType } from '@/app/constants/configuration/exchangeRateSourceType'
import { ExchangeRateConfigurationModel } from '@/app/models/configuration/exchangeRateConfigurationModel'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { formatQuarter } from 'app/common/dateHelper'
import { formatDate } from '@/modules/shared/utils/date'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import styles from './styles.module.scss'

type Props = {
  sourceType: ExchangeRateSourceType
  rates: ExchangeRateConfigurationModel[]
  sortKey: string
  sortAsc: boolean
  onHeaderClick: (key: string) => void
}

export const Table = ({
  sourceType,
  rates,
  sortKey,
  sortAsc,
  onHeaderClick,
}: Props) => {
  const { tSafe } = useLocalize()

  const columns: TableHeaderColumn[] = []

  if (sourceType === ExchangeRateSourceType.Ecb) {
    columns.push({
      title: tSafe('configuration.exchangeRates.date', 'Date'),
      key: 'exchangeRateDate',
      type: 'date',
      sort: true,
    })
  }

  if (sourceType === ExchangeRateSourceType.QuarterWge) {
    columns.push({
      title: tSafe('configuration.exchangeRates.quarter', 'Quarter'),
      key: 'exchangeRateDate',
      type: 'date',
      sort: true,
    })
  }

  columns.push(
    {
      title: tSafe('configuration.exchangeRates.currency', 'Currency'),
      key: 'currencyName',
      type: 'string',
      sort: true,
    },
    {
      title: tSafe('configuration.exchangeRates.currency-code', 'CurrencyCode'),
      key: 'currencyCode',
      type: 'string',
      sort: true,
    },
    {
      title: tSafe('configuration.exchangeRates.exchange-rate', 'ExchangeRate'),
      key: 'exchangeRate',
      type: 'number',
      sort: true,
    },
  )

  return (
    <div
      className={clx('tw-w-full tw-min-h-0 tw-flex-1 tw-overflow-auto', styles.tableWrapper)}
      data-test-id="table-exchange-rate"
    >
      <table className="tw-w-full tw-text-sm">
        <TableHeader
          columns={columns}
          disabledCheckboxToggle
          fontSize="sm"
          sortConfig={{ key: sortKey, direction: sortAsc ? 'asc' : 'desc' }}
          onHeaderClick={onHeaderClick}
        />
        <tbody>
          {rates.map((rate, i) => (
            <tr key={i}>
              {sourceType === ExchangeRateSourceType.QuarterWge && (
                <td>{formatQuarter(rate.exchangeRateDate)}</td>
              )}
              {sourceType === ExchangeRateSourceType.Ecb && (
                <td>{formatDate(rate.exchangeRateDate, 'YYYY-MM-DD')}</td>
              )}
              <td>{rate.currency.name}</td>
              <td>{rate.currency.code}</td>
              <td>{rate.exchangeRate.toFixed(6)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
